// The meeting room's stage pieces: people's and Jabberr's tiles, the status icon, and the header with its live clock.
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { ListenMode } from '@jabberr/core';
import { Avatar } from './Avatar.js';
import { JabberrModeBadge } from './JabberrModeBadge.js';
import { DuplexStatus } from './DuplexStatus.js';
import { avatarUrl } from '../lib/appearance.js';
import { talkModeHint, talkModeLabel } from '../lib/plain.js';
import { formatElapsed } from '../lib/room.js';
import { BOT_IDENTITY, BOT_NAME } from '../pages/RoomPanel.js';
import type { JabberrStatus } from '../state/jabberrStatus.js';
import type { DuplexSignals } from '../state/duplexSignals.js';

/** A small status indicator component for Jabberr tiles and the status bar. */
export function StatusIndicator({ state }: { state: string }) {
  switch (state) {
    case 'hearing':
      return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
        </svg>
      );
    case 'listening':
      return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
        </svg>
      );
    default:
      return <span className="status-dot" aria-hidden="true"></span>;
  }
}

const MIC_OFF = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M16 10a4 4 0 0 1-8 0"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
);

function LevelBars() {
  return (
    <span className="level-bars" aria-hidden="true">
      <span className="bar bar-1"></span>
      <span className="bar bar-2"></span>
      <span className="bar bar-3"></span>
    </span>
  );
}

/** A person's block on the stage: it glows in the speaking colour while they talk. */
export function PersonTile({ name, avatarName, avatarId, speaking, muted }: { name: string; avatarName: string; avatarId: string; speaking: boolean; muted: boolean }) {
  return (
    <div className={`room-tile ${speaking ? 'room-tile-speaking' : ''}`} data-tone={speaking ? 'speaking' : 'idle'}>
      <span className="room-tile-avatar">
        <Avatar name={avatarName} id={avatarId} size={96} />
      </span>
      <div className="room-tile-content">
        <span className="room-name">{name}</span>
        {speaking ? (
          <span className="room-tile-state" aria-label="speaking">Speaking <LevelBars /></span>
        ) : muted ? (
          <span className="room-tile-state room-tile-muted" aria-label="muted">{MIC_OFF} Muted</span>
        ) : (
          <span className="room-tile-state room-tile-listening">Listening</span>
        )}
      </div>
    </div>
  );
}

/** Jabberr's block: the speaking glow when his voice plays, the thinking colour while he prepares a
 *  reply or holds one for an opening, a quiet ring while he hears someone. */
export function JabberrTile({ status, listenMode, avatarId, duplex }: { status: JabberrStatus | null; listenMode: ListenMode | null; avatarId?: string; duplex?: DuplexSignals | null }) {
  const tone = duplex ? 'idle' : status?.tone ?? 'idle';
  return (
    <div className={`room-tile room-tile-bot ${tone === 'speaking' ? 'room-tile-speaking' : ''}`} data-tone={tone}>
      <span className={`room-tile-avatar ${duplex ? 'duplex-avatar' : ''}`} data-jabberr-speaking={duplex?.jabberrSpeaking} data-you-speaking={duplex?.peopleSpeaking}>
        <Avatar name={BOT_NAME} id={BOT_IDENTITY} size={96} bot avatarId={avatarId} />
        <JabberrModeBadge mode={listenMode} />
      </span>
      <div className="room-tile-content">
        <span className="room-name">{BOT_NAME}</span>
        {duplex ? <DuplexStatus signals={duplex} /> : <>
          {!status && listenMode ? (
            <span className="room-tile-state">{talkModeLabel(listenMode)} · {talkModeHint(listenMode).replace(/^Jabberr /, '').replace(/\.$/, '')}</span>
          ) : null}
          {status && (
            <span className="room-tile-state" aria-label={status.text}>
              {status.text}
              {status.mode === 'pulsing-dots' && (
                <span className="pulsing-dots" aria-hidden="true">
                  <span className="dot"></span><span className="dot"></span><span className="dot"></span>
                </span>
              )}
              {status.mode === 'level-bars' && <LevelBars />}
            </span>
          )}
        </>}
      </div>
    </div>
  );
}

/** "Live 12:04", ticking once a second. A component, so its hooks run on every render of itself. */
function LiveChip({ startedAt }: { startedAt: string }) {
  const [elapsed, setElapsed] = useState(() => formatElapsed(startedAt));
  useEffect(() => {
    const timer = setInterval(() => setElapsed(formatElapsed(startedAt)), 1000);
    return () => clearInterval(timer);
  }, [startedAt]);
  return (
    <span className="room-pill room-pill-rec" title="Jabberr keeps a transcript of this meeting">
      <span className="live-dot" aria-hidden="true" /> Recording · {elapsed}
    </span>
  );
}

export function MeetingHeader({
  meeting,
  project,
  onRename,
  avatarId,
  appearance,
}: {
  meeting: { title: string; startedAt: string; status: string; recordingId: string | null };
  avatarId: string;
  appearance?: ReactNode;
  project: { id: string; name: string } | null;
  onRename: (title: string) => void;
}) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(meeting.title);
  const live = meeting.status === 'live';

  const saveTitle = (): void => {
    const title = titleDraft.trim();
    if (title && title !== meeting.title) onRename(title);
    setEditingTitle(false);
  };

  return (
    <header className="room-header">
      <div className="room-header-left">
        <img className="room-header-avatar" src={avatarUrl(avatarId)} alt="" aria-hidden="true" />
        <div className="room-header-titles">
        {editingTitle ? (
          <input
            autoFocus
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={saveTitle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveTitle();
              if (e.key === 'Escape') { setTitleDraft(meeting.title); setEditingTitle(false); }
            }}
            aria-label="Meeting title"
            maxLength={200}
            className="room-title-input"
          />
        ) : (
          <button
            type="button"
            className="room-header-title"
            title="Rename the meeting"
            onClick={() => { setTitleDraft(meeting.title); setEditingTitle(true); }}
          >
            {meeting.title}
          </button>
        )}
        {project && <Link to={`/p/${project.id}`} className="room-project-link">{project.name}</Link>}
        </div>
      </div>
      <div className="room-header-right">
        {live ? <LiveChip startedAt={meeting.startedAt} /> : <span className="room-pill">Ended</span>}
        {live ? appearance : null}
        {!live && meeting.recordingId && <Link to={`/r/${meeting.recordingId}`} className="btn btn-primary">Open the transcript</Link>}
      </div>
    </header>
  );
}
